import { GlobalAlert } from '@thoughtspot/global-alert';
import { useTranslation } from '@thoughtspot/i18n';
import {
    PropertyMap,
    UserAction,
    userActionTypeEnum,
} from '@thoughtspot/metrics';
import { Modal, ModalSize } from '@thoughtspot/radiant-react/widgets/modal';
import { Typography } from '@thoughtspot/radiant-react/widgets/typography';
import { InviteModalType } from '@thoughtspot/session-service';
import cx from 'classnames';
import React, { useEffect, useState } from 'react';
import { getEmailDomain, isEmail } from '../../email/email.util';
import { InvitedUsersInfo } from '../../setup-hub/setup-hub-interface';
import { InviteSource, InviteUsersModalProps } from '../invite-users.interface';
import { InviteUsersContent } from '../invite-users-content/invite-users-content';
import {
    useInviteDescriptionState,
    useInvitedUsersState,
} from '../use-invite-users-state';
import styles from './invite-users-modal.module.scss';

export const sendMixpanelEventForInviteUsers = (
    emails: Array<string>,
    collectEvent: (userAction: UserAction, property: PropertyMap) => void,
    optionalmsg: string,
    invitedUserInfo: InvitedUsersInfo,
    source: InviteSource,
) => {
    const uniqueDomains = new Set<string>();
    emails.forEach(email => uniqueDomains.add(getEmailDomain(email)));
    collectEvent(new UserAction(userActionTypeEnum.INVITE_OTHERS), {
        numInvitedUsers: emails.length,
        hasOptionalMessage: !!optionalmsg,
        uniqueEmailDomains: Array.from(uniqueDomains),
        sharedGroupId: invitedUserInfo.sharedGroupId,
        userIds: invitedUserInfo.userIds,
        source,
    });
};

export const InviteUsersModal: React.FC<InviteUsersModalProps> = ({
    isModalOpen,
    onEmailAdd,
    onModalClose,
    onModalConfirm,
    inviteModalType,
    supportEmail,
    collectEvent,
    errorMsg,
    setIsToastVisible,
    isCopilotEnabled = false,
    source,
}) => {
    const { t } = useTranslation();
    const [isOpen, setIsOpen] = useState(false);
    const [isInviteInProgress, setIsInviteInProgress] = React.useState(false);
    const [inputEmail, setInputEmail] = useState('');
    const [inputEmailHasError, setInputEmailHasError] = useState(false);
    const {
        invitedUsers,
        addInvitedUser,
        removeInvitedUser,
        resetInvitedUsers,
    } = useInvitedUsersState(onEmailAdd);
    const { description, updateDescription } = useInviteDescriptionState();

    useEffect(() => {
        setIsOpen(isModalOpen);
        if (isModalOpen) {
            resetInvitedUsers();
            setInputEmail('');
            setInputEmailHasError(false);
        }
    }, [isModalOpen]);

    const onConfirm = async (): Promise<boolean> => {
        let rawEmails;
        if (invitedUsers.length === 0) {
            if (!isEmail(inputEmail)) {
                setInputEmailHasError(true);
                return false;
            }
            rawEmails = [inputEmail];
        } else {
            rawEmails = invitedUsers.map(user => user.address);
        }
        setIsInviteInProgress(true);
        const invitedUserInfo: InvitedUsersInfo = await onModalConfirm(
            rawEmails,
            description,
        );
        if (invitedUserInfo) {
            setIsToastVisible(true);
            sendMixpanelEventForInviteUsers(
                rawEmails,
                collectEvent,
                description,
                invitedUserInfo,
                source,
            );
        }
        setIsInviteInProgress(false);
        // Reset description to empty
        updateDescription('');
        return true;
    };

    const onDismiss = () => {
        // Reset description to empty
        updateDescription('');
        onModalClose();
    };

    return (
        <div data-testid="inviteModal">
            {inviteModalType === InviteModalType.EMAIL_REFERRAL ? (
                <Modal
                    isOpen={isOpen}
                    size={
                        isCopilotEnabled ? ModalSize.MEDIUM3 : ModalSize.MEDIUM
                    }
                    title={t('homehub.task.finish.invite')}
                    confirmButton={{
                        text: t('homehub.invite_others.confirm_button'),
                        tooltip: t('homehub.invite_others.tooltip'),
                        onConfirm,
                        isLoading: isInviteInProgress,
                    }}
                    dismissButton={{
                        text: t('Cancel'),
                    }}
                    onDismiss={onDismiss}
                    customFooterClassName={cx({
                        [styles.copilotCustomFooter]: isCopilotEnabled,
                    })}
                >
                    <InviteUsersContent
                        invitedUsers={invitedUsers}
                        onEmailAdd={email => addInvitedUser(email)}
                        onEmailDelete={email => removeInvitedUser(email)}
                        onDescriptionAdd={(desc: string) =>
                            updateDescription(desc)
                        }
                        inputEmail={inputEmail}
                        setInputEmail={setInputEmail}
                        inputEmailHasError={inputEmailHasError}
                        setInputEmailHasError={setInputEmailHasError}
                        isCopilotEnabled={isCopilotEnabled}
                    />
                </Modal>
            ) : (
                <Modal
                    isOpen={isOpen}
                    size={ModalSize.SMALL}
                    title={t('homehub.task.finish.invite')}
                    dismissButton={{
                        text: t('OK'),
                    }}
                    onDismiss={onModalClose}
                >
                    <Typography variant="p">
                        {t('homehub.free_trial_invite', {
                            adminEmail: supportEmail,
                        })}
                    </Typography>
                </Modal>
            )}
        </div>
    );
};
